import Checkbox, { BlueCheckbox } from '@/gf/components/Checkbox'
import Field from '@/gf/components/Field'
import PriceInput from '@/gf/components/inputs/PriceV2'
import ModalForm from '@/gf/components/ModalForm'
import Spinner from '@/gf/components/Spinner'
import TextArea from '@/gf/components/TextArea'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import MoneyM from '@/gf/modules/Money'
import { InformationCircleIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Money } from '../../../types'
import { refundStoreOrder } from '../../modules/api'
import { useOrderContext } from './context'

type Form = {
  amount: Money | null
  cancelOrder: boolean
  notifyCustomer: boolean
  comments: string
}

const initialForm = {
  amount: null,
  cancelOrder: true,
  notifyCustomer: true,
  comments: '',
}

const zeroPrice = MoneyM.fromDecimal(0, 'USD')

// The width of the credit memo's `description` column, the narrowest place a refund reason
// lands across the two refund rails.
const REASON_MAX_LENGTH = 255

const RefundOrderModal = ({
  refundableAmount,
  onRefund,
}: {
  refundableAmount: Money
  onRefund: () => void
}) => {
  const { storeOrderId, storeOrderOld } = useOrderContext()
  const [spinnerLive, spinner] = useToggle()
  const navigate = useNavigate()
  const [_, msgr] = useMsgs()
  const [fields, setFields] = useState<Form>(initialForm)
  const reset = () => setFields(initialForm)
  const update = (updates: Partial<Form>) => setFields({ ...fields, ...updates })
  const fullRefund = !!fields.amount && MoneyM.equals(fields.amount, refundableAmount)
  const close = () => navigate(`/orders/${storeOrderId}`)

  const refund = async () => {
    spinner.on()
    const changeStoreOrderState = fullRefund && fields.cancelOrder

    const result = await refundStoreOrder(
      storeOrderId,
      fields.amount || zeroPrice,
      fields.comments,
      changeStoreOrderState,
      fields.notifyCustomer
    )

    if (result === 'success')
      msgr.add(
        `Order refunded.${fields.notifyCustomer ? ' Email sent to customer.' : ''}`,
        'positive'
      )
    else msgr.add(result.message, 'negative')

    close()
    spinner.off()
    reset()
    onRefund()
  }

  return (
    <ModalForm
      open
      onClose={() => {
        close()
        reset()
      }}
      title="Refund"
      onSubmit={(e) => {
        e.preventDefault()
        refund()
      }}
      submitButtonText="Submit"
      submitButtonDisabled={spinnerLive}
    >
      {spinnerLive ? (
        <Spinner />
      ) : (
        <div className="divide-y">
          <div className="space-y-4 pb-6">
            <p>Pressing submit below will refund the customer the amount indicated.</p>

            <Field label="Amount">
              <div className="flex gap-2 items-center">
                <div className="w-40">
                  <PriceInput
                    required
                    price={fields.amount}
                    onChange={(amt) => update({ amount: amt })}
                    min={0.01}
                    max={MoneyM.toDecimal(refundableAmount)}
                  />
                </div>

                <span>
                  of <span className="font-medium">{MoneyM.format(refundableAmount)}</span> total
                </span>
              </div>
            </Field>

            {storeOrderOld.state !== 'REFUNDED' && fullRefund && (
              <div className="space-y-2">
                <Checkbox
                  label="Cancel order"
                  value="Close order"
                  checked={fields.cancelOrder}
                  onChange={({ target }) => update({ cancelOrder: target.checked })}
                />

                <div className="px-2 py-1 inline-flex items-center gap-x-2 text-sm bg-gray-50 rounded border border-gray-200">
                  <InformationCircleIcon className="w-5 h-5 shrink-0" />
                  Don&apos;t check this box if more shipments need to be created.
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 space-y-4">
            {/*
              GEA-8803: the reason is its own field, asked whether or not the customer is
              notified. It used to live inside the notification block and was discarded when
              the email was not sent, so a refund explained but not announced recorded nothing.
            */}
            <Field
              label="What is this refund for?"
              desc="Optional. Recorded on the order timeline, which your customer can see."
              htmlFor="refund-reason"
            >
              <TextArea
                id="refund-reason"
                prose={false}
                rows={3}
                maxLength={REASON_MAX_LENGTH}
                value={fields.comments}
                placeholder="e.g. Returned core charge on the hydraulic pump"
                onChange={({ target }) => update({ comments: target.value })}
              />
            </Field>

            <BlueCheckbox
              label="Notify Customer (sends email)"
              id="send-approve-email"
              checked={fields.notifyCustomer}
              onChange={({ target }) => update({ notifyCustomer: target.checked })}
            />

            {fields.notifyCustomer && (
              <p className="text-sm text-gray-500">
                The reason above will be included in the customer notification.
              </p>
            )}
          </div>
        </div>
      )}
    </ModalForm>
  )
}

export default RefundOrderModal
